import type { PlanDefinition, PlanDraft } from "@weights/plan-reading";
import { cellKey } from "./diff.js";
import { sameChips } from "./draft.js";
import { labelKey } from "./lib.js";

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export interface Rebased {
  draft: PlanDraft;
  /** Cells both sides changed: `${r}:${columnId}` → the other device's chips. Ours won. */
  conflicts: Map<string, string[]>;
}

/**
 * Our unsaved edits redone on top of the server's newer draft (grid doc §4: "ours wins, marked").
 * `base` is what we last saved, `ours` what is on screen, `theirs` what the server has now.
 */
export function rebase(base: PlanDraft, ours: PlanDraft, theirs: PlanDraft): Rebased {
  const draft = structuredClone(theirs);
  const conflicts = new Map<string, string[]>();
  if (ours.title !== base.title) draft.title = ours.title;
  if (!same(ours.columns, base.columns)) draft.columns = structuredClone(ours.columns);
  if (!same(ours.notes, base.notes)) draft.notes = structuredClone(ours.notes);
  if (ours.basedOn !== base.basedOn) {
    if (ours.basedOn) draft.basedOn = ours.basedOn;
    else delete draft.basedOn;
  }

  for (let r = 0; r < 7; r++) {
    const o = ours.days[r];
    const b = base.days[r];
    const t = draft.days[r];
    if (!o || !t) continue;
    if (o.location !== (b?.location ?? "")) t.location = o.location;
    const ids = new Set([...Object.keys(o.cells), ...Object.keys(b?.cells ?? {}), ...Object.keys(t.cells)]);
    for (const id of ids) {
      const ov = o.cells[id] ?? [];
      const bv = b?.cells[id] ?? [];
      const tv = t.cells[id] ?? [];
      if (sameChips(ov, bv)) continue;
      if (!sameChips(tv, bv) && !sameChips(tv, ov)) conflicts.set(cellKey(r, id), [...tv]);
      if (ov.length) t.cells[id] = [...ov];
      else delete t.cells[id];
    }
  }

  if (!same(ours.sessions, base.sessions)) {
    const baseByKey = new Map(base.sessions.map((s) => [labelKey(s.label), s]));
    const oursKeys = new Set(ours.sessions.map((s) => labelKey(s.label)));
    let sessions: PlanDefinition[] = draft.sessions.filter((s) => {
      const key = labelKey(s.label);
      return !(baseByKey.has(key) && !oursKeys.has(key));
    });
    for (const s of ours.sessions) {
      const key = labelKey(s.label);
      if (same(baseByKey.get(key), s)) continue;
      const at = sessions.findIndex((x) => labelKey(x.label) === key);
      if (at >= 0) sessions[at] = structuredClone(s);
      else sessions = [...sessions, structuredClone(s)];
    }
    draft.sessions = sessions;
  }

  const ids = new Set(draft.columns.map((c) => c.id));
  for (const day of draft.days) for (const id of Object.keys(day.cells)) if (!ids.has(id)) delete day.cells[id];
  return { draft, conflicts };
}
